import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { API } from "@/lib/api";

const KEY = "aurora_consent";
export const OPEN_EVT = "aurora:cookie-prefs";
export const openCookiePrefs = () => window.dispatchEvent(new Event(OPEN_EVT));
const read = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };

const visitSid = () => {
  let s = sessionStorage.getItem("aurora_visit_sid");
  if (!s) { s = `v_${crypto.randomUUID().replace(/-/g, "")}`; sessionStorage.setItem("aurora_visit_sid", s); }
  return s;
};

const VisitorTracker = ({ enabled }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return;
    axios.post(`${API}/visit`, { sid: visitSid(), path: pathname.slice(0, 300), referrer: document.referrer.slice(0, 500) }).catch(() => {});
  }, [enabled, pathname]);
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(() => { if (document.visibilityState === "visible") axios.post(`${API}/visit`, { sid: visitSid(), ping: true }).catch(() => {}); }, 30000);
    return () => clearInterval(t);
  }, [enabled]);
  return null;
};

const Toggle = ({ on, onChange, disabled, testid }) => (
  <button type="button" data-testid={testid} disabled={disabled} onClick={() => onChange(!on)}
    className={`w-10 h-6 rounded-full relative transition-colors ${on ? "bg-[#10b981]" : "bg-[#cfd6d9]"} disabled:opacity-60`}>
    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
  </button>
);

export const CookieConsent = () => {
  const { tr } = useLang();
  const [consent, setConsent] = useState(read);
  const [open, setOpen] = useState(!read());
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: consent?.analytics ?? false, marketing: consent?.marketing ?? false });
  useEffect(() => {
    const h = () => { setOpen(true); setCustom(true); };
    window.addEventListener(OPEN_EVT, h);
    return () => window.removeEventListener(OPEN_EVT, h);
  }, []);
  const save = (p) => {
    const c = { necessary: true, ...p, ts: new Date().toISOString(), v: 1 };
    localStorage.setItem(KEY, JSON.stringify(c)); setConsent(c); setPrefs(p); setOpen(false); setCustom(false);
  };
  return (
    <>
      <VisitorTracker enabled={!!consent?.analytics} />
      {open && (
        <div data-testid="cookie-banner" className="fixed bottom-0 inset-x-0 z-[10000] bg-white border-t-2 border-[#2B3036] shadow-2xl animate-in slide-in-from-bottom duration-300">
          <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col lg:flex-row gap-5 lg:items-end">
            <div className="flex-1">
              <div className="flex items-center gap-2 font-display text-base"><Cookie className="w-4 h-4 text-[#E0262C]" /> {tr("Rispettiamo la tua privacy", "We respect your privacy")}</div>
              <p className="text-xs text-[#5b6168] mt-1.5 max-w-3xl">
                {tr("Usiamo cookie tecnici necessari e, solo con il tuo consenso, cookie analitici per statistiche anonime sulle visite (IP anonimizzato, dispositivo, pagine, durata; conservati max 90 giorni).", "We use necessary technical cookies and, only with your consent, analytics cookies for anonymous visit statistics (anonymised IP, device, pages, duration; kept max 90 days).")}{" "}
                <Link to="/cookie-policy" data-testid="cookie-policy-link" className="underline">Cookie Policy</Link>
              </p>
              {custom && (
                <div data-testid="cookie-custom" className="mt-4 grid sm:grid-cols-3 gap-3">
                  {[["necessary", tr("Necessari", "Necessary"), tr("Sessione, sicurezza, lingua, carrello. Sempre attivi.", "Session, security, language, cart. Always on.")],
                    ["analytics", tr("Analitici", "Analytics"), tr("Statistiche visitatori anonimizzate.", "Anonymised visitor statistics.")],
                    ["marketing", "Marketing", tr("Pixel e remarketing (attualmente non utilizzati).", "Pixels and remarketing (currently not used).")]].map(([k, t, d]) => (
                    <div key={k} className="border border-[#e4e4e7] p-3 flex gap-3 items-start">
                      <div className="flex-1"><div className="text-xs font-semibold">{t}</div><div className="text-[11px] text-[#71717a]">{d}</div></div>
                      <Toggle testid={`cookie-toggle-${k}`} on={k === "necessary" || prefs[k]} disabled={k === "necessary"} onChange={(v) => setPrefs({ ...prefs, [k]: v })} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {custom ? (
                <button type="button" data-testid="cookie-save" onClick={() => save(prefs)} className="h-10 px-4 text-sm border border-[#2B3036] hover:bg-[#f4f6f7]">{tr("Salva preferenze", "Save preferences")}</button>
              ) : (
                <button type="button" data-testid="cookie-customize" onClick={() => setCustom(true)} className="h-10 px-4 text-sm border border-[#cfd6d9] hover:border-[#2B3036]">{tr("Personalizza", "Customise")}</button>
              )}
              <button type="button" data-testid="cookie-reject" onClick={() => save({ analytics: false, marketing: false })} className="h-10 px-4 text-sm border border-[#2B3036] hover:bg-[#f4f6f7]">{tr("Solo necessari", "Necessary only")}</button>
              <button type="button" data-testid="cookie-accept" onClick={() => save({ analytics: true, marketing: true })} className="h-10 px-5 text-sm text-white bg-[#2B3036] hover:bg-[#E0262C] transition-colors">{tr("Accetta tutti", "Accept all")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
